/**
 * SAT superscore.
 *
 * The digital SAT (international since March 2023, US since March 2024) reports a
 * Reading and Writing section score and a Math section score, each 200–800 in 10-point
 * increments, and a total of 400–1600 (College Board, "How Are Scores Calculated?",
 * satsuite.collegeboard.org, checked 2026).
 *
 * A college that superscores takes the highest Reading and Writing score and the
 * highest Math score across the test dates it receives and adds them. College Board does
 * not compute a superscore itself — scores are sent by test date, and each college sets
 * its own policy (College Board, "What's an SAT Superscore?").
 */

export interface SatSitting {
  readingWriting: number;
  math: number;
}

export type SatIssue = "not-a-number" | "out-of-range" | "not-multiple-of-10";

/** Why a typed section score is not a possible SAT score, or null when it is. */
export function satScoreIssue(value: number | null): SatIssue | null {
  if (value === null || !Number.isFinite(value)) {
    return "not-a-number";
  }
  if (value < 200 || value > 800) {
    return "out-of-range";
  }
  if (!Number.isInteger(value) || value % 10 !== 0) {
    return "not-multiple-of-10";
  }
  return null;
}

export interface SatBest {
  score: number;
  /** Index of the first sitting that earned it. */
  sitting: number;
}

export interface SatSuperscore {
  readingWriting: SatBest;
  math: SatBest;
  superscore: number;
  /** The best total earned on a single test date. */
  bestSingle: SatBest;
}

export function satSuperscore(
  sittings: readonly SatSitting[]
): SatSuperscore | null {
  if (sittings.length === 0) {
    return null;
  }
  let readingWriting: SatBest = { score: -1, sitting: 0 };
  let math: SatBest = { score: -1, sitting: 0 };
  let bestSingle: SatBest = { score: -1, sitting: 0 };
  for (const [index, sitting] of sittings.entries()) {
    if (sitting.readingWriting > readingWriting.score) {
      readingWriting = { score: sitting.readingWriting, sitting: index };
    }
    if (sitting.math > math.score) {
      math = { score: sitting.math, sitting: index };
    }
    const total = sitting.readingWriting + sitting.math;
    if (total > bestSingle.score) {
      bestSingle = { score: total, sitting: index };
    }
  }
  return {
    readingWriting,
    math,
    superscore: readingWriting.score + math.score,
    bestSingle,
  };
}
